import { MedplumClient } from "@medplum/core";
import {
  Coding,
  Encounter,
  EncounterParticipant,
  Patient,
  QuestionnaireResponse,
  RelatedPerson,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import { LOINCCodes } from "@/fhir/enums/loinc";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { EncounterPractitionerType, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { decimalWeeksToWeeksDays } from "@/pregnancy";
import { getDisplayStringAsync, joinReferenceString, joinTokenString } from "@/utils/medplum";

import { MedsAndTherapies } from "../components/NewbornPostpartumEncounter/components/MedicationAndTherapiesDrawer";
import {
  getGynecologicalEncounterVitalsSignQuestionnaireResponse,
  getNewbornEncounterBirthDetailsQuestionnaireResponse,
  getNewbornEncounterSizeQuestionnaireResponse,
  getNewbornIPPEncounterFeedingQuestionnaireResponse,
  getNewbornIPPEncounterVitalsSignQuestionnaireResponse,
  getNewbornIPPGestationalAgeQuestionnaireResponse,
  getNewbornIPPMedicationAndTherapiesQuestionnaireResponse,
  getNewbornPostpartumEncounterSizeQuestionnaireResponse,
  getNewbornPostpartumEncounterStateOfNewbornQuestionnaireResponse,
  getNewbornPostpartumEncounterVitalsSignQuestionnaireResponse,
  getNewbornPostpartumInputsOutputsQuestionnaireResponse,
  getNewbornPostpartumMedicationAndTherapiesQuestionnaireResponse,
  getPostpartumActivityNutritionQuestionnaireResponse,
  getPostpartumEmotionalHealthQuestionnaireResponse,
  getPostpartumFeedingQuestionnaireResponse,
  getPostpartumLochiaQuestionnaireResponse,
  getPostpartumMedicationAndTherapiesQuestionnaireResponse,
  getPostpartumOutputQuestionnaireResponse,
  getPostpartumSexualActivityQuestionnaireResponse,
  getPostpartumSleepQuestionnaireResponse,
  getPostpartumSocialHealthQuestionnaireResponse,
  getPostpartumVitalsQuestionnaireResponse,
} from "../utils/questionnaire-response";
import { GynecologicalEncounterVitalsSignFormType } from "../zod-schemas/gyn-encounter";
import {
  NewbornEncounterBirthDetailsFormType,
  NewbornEncounterFeedingFormType,
  NewbornEncounterSizeFormType,
  NewbornIppEncounterVitalsSignFormType,
  NewbornIPPGestationalAgeFormType,
  NewbornIPPMedicationAndTherapiesFormType,
} from "../zod-schemas/newborn-ipp-encounter";
import {
  NewbornPostpartumEncounterStateOfNewbornFormType,
  NewbornPostpartumEncounterVitalsSignFormType,
} from "../zod-schemas/newborn-postpartum-encounter";
import {
  PostpartumMedsAndTherapies,
  PostpartumVitalsFormType,
} from "../zod-schemas/postpartum-encounter";

type CreateBirthDetailsQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornEncounterBirthDetailsFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createBirthDetailsQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateBirthDetailsQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornIPPBirthDetails,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornEncounterBirthDetailsQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) return await medplum.updateResource(updatedQuestionnaireResponse);

  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateSizeQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornEncounterSizeFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createSizeQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateSizeQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornIPPSize,
  });
  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornEncounterSizeQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id)
    return await medplum.updateResource(updatedQuestionnaireResponse, {});

  return await medplum.createResource(updatedQuestionnaireResponse);
};

export const createNewbornPostpartumSizeQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateSizeQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornPostpartumSize,
  });
  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornPostpartumEncounterSizeQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  // For amendments, always update the existing QuestionnaireResponse
  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse, {});
  }

  // For new forms, create a new QuestionnaireResponse
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateVitalsSignQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornIppEncounterVitalsSignFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createVitalsSignQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateVitalsSignQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornIPPVitalsSign,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornIPPEncounterVitalsSignQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) return await medplum.updateResource(updatedQuestionnaireResponse);

  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateNewbornPostpartumVitalsSignQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornPostpartumEncounterVitalsSignFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createNewbornPostpartumVitalsSignQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateNewbornPostpartumVitalsSignQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornPostpartumVitalsSign,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;
  const updatedQuestionnaireResponse = getNewbornPostpartumEncounterVitalsSignQuestionnaireResponse(
    {
      form: values,
      clientId,
      practitionerId,
      encounterId,
      questionnaireId: questionnaire.id,
      status,
      questionnaireResponse,
    },
  );

  // For amendments, always update the existing QuestionnaireResponse
  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }

  // For new forms, create a new QuestionnaireResponse
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateFeedingQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornEncounterFeedingFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createFeedingQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateFeedingQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornIPPFeeding,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornIPPEncounterFeedingQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) return await medplum.updateResource(updatedQuestionnaireResponse);

  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateMedicationAndTherapiesQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornIPPMedicationAndTherapiesFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createMedicationAndTherapiesQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateMedicationAndTherapiesQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornIPPMedicationAndTherapies,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornIPPMedicationAndTherapiesQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (
    !questionnaireResponse?.id &&
    (!updatedQuestionnaireResponse.item || updatedQuestionnaireResponse.item.length === 0)
  ) {
    return undefined;
  }

  if (questionnaireResponse?.id) return await medplum.updateResource(updatedQuestionnaireResponse);

  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateNewbornPostpartumMedicationAndTherapiesQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: MedsAndTherapies;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createNewbornPostpartumMedicationAndTherapiesQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateNewbornPostpartumMedicationAndTherapiesQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornPostpartumMedicationAndTherapies,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse =
    getNewbornPostpartumMedicationAndTherapiesQuestionnaireResponse({
      form: values,
      clientId,
      practitionerId,
      encounterId,
      questionnaireId: questionnaire.id,
      status,
      questionnaireResponse,
    });

  // For amendments, always update the existing QuestionnaireResponse
  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }

  // For new forms, create a new QuestionnaireResponse
  return await medplum.createResource(updatedQuestionnaireResponse);
};

/**
 * Resolves the baby patient resource ID from a mother patient for LMP lookup.
 * Tries multiple strategies in order:
 * 1. Patient link (mother -> baby)
 * 2. RelatedPerson with CHILD relationship
 * 3. Falls back to mother patient ID if no baby found
 *
 * @param medplum - Medplum client instance
 * @param patient - The mother patient
 * @returns The baby patient ID, or mother patient ID as fallback
 */
async function resolveBabyPatientResourceIdForLMP(
  medplum: MedplumClient,
  patient: Patient,
): Promise<string> {
  if (!patient.id) {
    throw new Error("Patient ID is missing");
  }

  // Strategy 1: Check if patient has a link to a baby Patient (mother -> baby)
  const linkedBabyId = patient?.link?.[0]?.other?.reference?.split("/")[1];
  if (linkedBabyId) {
    try {
      const babyPatient = (await medplum.searchOne("Patient", {
        _id: linkedBabyId,
      })) as Patient | undefined;
      if (babyPatient?.id) {
        return babyPatient.id;
      }
    } catch {
      // Baby patient doesn't exist, continue to next strategy
    }
  }

  // Strategy 2: Check RelatedPerson with CHILD relationship
  try {
    const relatedPerson = (await medplum.searchOne("RelatedPerson", {
      patient: `Patient/${patient.id}`,
      relationship: "CHILD",
    })) as RelatedPerson | undefined;

    if (relatedPerson?.extension) {
      const babyRef = relatedPerson.extension.find(
        (ext) => ext.url === "http://hl7.org/fhir/StructureDefinition/artifact-relatedArtifact",
      )?.valueReference?.reference;

      if (babyRef) {
        const babyId = babyRef.split("/")[1];
        if (babyId) {
          try {
            const babyPatient = (await medplum.searchOne("Patient", {
              _id: babyId,
            })) as Patient | undefined;
            if (babyPatient?.id) {
              return babyPatient.id;
            }
          } catch {
            // Baby patient doesn't exist, continue to fallback
          }
        }
      }
    }
  } catch {
    // RelatedPerson lookup failed, continue to fallback
  }

  // Strategy 3: Fallback to mother patient ID
  return patient.id;
}

type CreateGestationalAgeQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornIPPGestationalAgeFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createGestationalAgeQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateGestationalAgeQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornIPPGestationalAge,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  // Fetch LMP gestational age Observation for audit trail
  let lmpGestationalAge: number | undefined;
  try {
    const patient = await medplum.searchOne("Patient", {
      _id: clientId,
    });

    if (patient) {
      // Get baby patient resource ID
      const babyPatientResourceId = await resolveBabyPatientResourceIdForLMP(medplum, patient);

      // Search for LMP-created gestational age Observation
      const lmpObservation = await medplum.searchOne("Observation", {
        code: "http://loinc.org|18185-9",
        subject: `Patient/${babyPatientResourceId}`,
        encounter: `Encounter/${encounterId}`,
        _sort: "-_lastUpdated",
      });

      // Only use if it's derived from an Observation (LMP-created), not from a QuestionnaireResponse
      if (
        lmpObservation?.valueQuantity?.value &&
        lmpObservation.derivedFrom?.[0]?.reference?.startsWith("Observation/")
      ) {
        lmpGestationalAge = lmpObservation.valueQuantity.value;
      }
    }
  } catch (error) {
    // Silently fail - if we can't fetch LMP, just don't include it
    console.error("Failed to fetch LMP gestational age for QuestionnaireResponse:", error);
  }

  const updatedQuestionnaireResponse = getNewbornIPPGestationalAgeQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
    lmpGestationalAge,
  });

  if (
    !questionnaireResponse?.id &&
    (!updatedQuestionnaireResponse.item || updatedQuestionnaireResponse.item.length === 0)
  ) {
    return undefined;
  }

  if (questionnaireResponse?.id) return await medplum.updateResource(updatedQuestionnaireResponse);

  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateStateOfNewbornQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: NewbornPostpartumEncounterStateOfNewbornFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
  newbornStatesList: ValueSetExpansionContains[];
  sexOptionsList: ValueSetExpansionContains[];
};

export const createNewbornPostpartumStateOfNewbornQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
  newbornStatesList,
  sexOptionsList,
}: CreateStateOfNewbornQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornPostpartumStateOfNewborn,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse =
    getNewbornPostpartumEncounterStateOfNewbornQuestionnaireResponse({
      form: values,
      clientId,
      practitionerId,
      encounterId,
      questionnaireId: questionnaire.id,
      status,
      questionnaireResponse,
      newbornStatesList,
      sexOptionsList,
    });

  // For amendments, always update the existing QuestionnaireResponse
  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }

  // For new forms, create a new QuestionnaireResponse
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateInputsOutputsQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    inputOutputFeedingType: string;
    inputOutputFeedingSupplementAmount: number;
    inputOutputFeedingLength: number;
    inputOutputFeedingBehavior: string[];
    inputOutputVoiding: string[];
    inputOutputVoidingNormal: string;
    inputOutputVoidingNotNormal: string;
    inputOutputBowel: string[];
    inputOutputBowelNormal: string;
    inputOutputBowelNotNormal: string;
    inputOutputNotes: string;
    inputOutputFeedingSupplement?: string[];
    inputOutputFeedingSupplementAmountPumpedMilk?: number;
    inputOutputFeedingSupplementAmountUnitPumpedMilk?: Coding;
    inputOutputFeedingSupplementAmountDonorMilk?: number;
    inputOutputFeedingSupplementAmountUnitDonorMilk?: Coding;
    inputOutputFeedingSupplementAmountFormula?: number;
    inputOutputFeedingSupplementAmountUnitFormula?: Coding;
    inputOutputFeedingSupplementFormset?: Record<
      string,
      {
        inputOutputFeedingSupplementFormsetCode: string;
        inputOutputFeedingSupplementFormsetType: string[];
        inputOutputFormsetTypeFeedingSupplementAmountPumpedMilk: number;
        inputOutputFormsetTypeFeedingAmountUnitPumpedMilk: Coding;
        inputOutputFormsetTypeFeedingSupplementAmountDonorMilk: number;
        inputOutputFormsetTypeFeedingAmountUnitDonorMilk: Coding;
        inputOutputFormsetTypeFeedingSupplementAmountFormula: number;
        inputOutputFormsetTypeFeedingAmountUnitFormula: Coding;
      }
    >;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createNewbornPostpartumInputsOutputsQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateInputsOutputsQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.NewbornPostpartumInputOutput,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getNewbornPostpartumInputsOutputsQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  // For amendments, always update the existing QuestionnaireResponse
  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }

  // For new forms, create a new QuestionnaireResponse
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumFeedingQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumFeedingType?: string[];
    postpartumFeedingSupplement?: string[];
    postpartumFeedingWith?: string[];
    postpartumFeedingSupply?: string;
    postpartumFeedingPain?: string;
    postpartumFeedingRedness?: string;
    postpartumFeedingDucts?: string;
    postpartumFeedingNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumFeedingQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumFeedingQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumFeeding,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumFeedingQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumLochiaQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumLochiaType?: string[];
    postpartumLochiaNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumLochiaQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumLochiaQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumLochia,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumLochiaQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumActivityNutritionQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumActivityNutritionType?: string[];
    postpartumActivityNutritionTypeOther?: string;
    postpartumActivityNutritionDietaryRestrictions?: string[];
    postpartumActivityNutritionBeveragesCaffeine?: string;
    postpartumActivityNutritionMeals?: string;
    postpartumActivityNutritionWater?: string;
    postpartumActivityNutritionBeverages?: string[];
    postpartumActivityNutritionNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumActivityNutritionQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumActivityNutritionQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumActivityNutrition,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumActivityNutritionQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumMedicationAndTherapiesQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: PostpartumMedsAndTherapies;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumMedicationAndTherapiesQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumMedicationAndTherapiesQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumMedicationAndTherapies,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumMedicationAndTherapiesQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumSleepQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumSleepHours?: number;
    postpartumSleepWakeUp?: string;
    postpartumSleepFallingAsleep?: string;
    postpartumSleepStayingAsleep?: string;
    postpartumSleepBreathing?: string;
    postpartumSleepNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumSleepQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumSleepQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumSleep,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumSleepQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumSocialHealthQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumSocialHealthSmoke?: string;
    postpartumSocialHealthSmokeAmount?: string;
    postpartumSocialHealthSmokeFrequency?: string;
    postpartumSocialHealthDrink?: string;
    postpartumSocialHealthDrinkAmount?: string;
    postpartumSocialHealthDrinkFrequency?: string;
    postpartumSocialHealthDrugs?: string;
    postpartumSocialHealthDrugsType?: string;
    postpartumSocialHealthWork?: string;
    postpartumSocialHealthWorkCapacity?: string;
    postpartumSocialHealthWorkFrequency?: string;
    postpartumSocialHealthSupport?: string;
    postpartumSocialHealthSupportBy?: string[];
    postpartumSocialHealthNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumSocialHealthQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumSocialHealthQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumSocialHealth,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumSocialHealthQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumEmotionalHealthQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumEmotionalHealthMood?: string[];
    postpartumEmotionalHealthTasks?: string;
    postpartumEmotionalHealthBonding?: string;
    postpartumEmotionalHealthSuicide?: string;
    postpartumEmotionalHealthHallucinations?: string;
    postpartumEmotionalHealthNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumEmotionalHealthQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumEmotionalHealthQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumEmotionalHealth,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumEmotionalHealthQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumSexualActivityQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    postpartumSexualActivityResumed?: string;
    postpartumSexualActivityDiscomfort?: string;
    postpartumSexualActivityPain?: string[];
    postpartumSexualActivityDryness?: string;
    postpartumSexualActivityContraception?: string;
    postpartumSexualActivityContraceptionMethod?: string[];
    postpartumSexualActivityIUD?: string;
    postpartumSexualActivityHormoneFree?: string[];
    postpartumSexualActivityProgesteroneContaining?: string[];
    postpartumSexualActivityFuture?: string;
    postpartumSexualActivityFutureWhen?: string;
    postpartumSexualActivityNotes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumSexualActivityQuestionnaireResponse = async ({
  medplum,
  values,
  clientId,
  practitionerId,
  encounterId,
  status,
  questionnaireResponse,
}: CreatePostpartumSexualActivityQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumSexualActivity,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  // If updating an existing resource, read the latest version first to avoid concurrent update conflicts
  let latestQuestionnaireResponse = questionnaireResponse;
  if (questionnaireResponse?.id) {
    try {
      latestQuestionnaireResponse = await medplum.readResource(
        "QuestionnaireResponse",
        questionnaireResponse.id,
      );
    } catch (error) {
      // If read fails, log for visibility and fall back to the provided questionnaireResponse
      // This helps diagnose concurrency, permission, or Medplum availability issues.

      console.error(
        "Failed to read latest QuestionnaireResponse for concurrency check",
        questionnaireResponse.id,
        error,
      );
      latestQuestionnaireResponse = questionnaireResponse;
    }
  }

  const updatedQuestionnaireResponse = getPostpartumSexualActivityQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse: latestQuestionnaireResponse,
  });

  if (latestQuestionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumOutputQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: {
    urineNormal?: boolean[];
    urineNormalDetails?: string[];
    urineNotNormalDetails?: string[];
    bowelNormal?: boolean[];
    bowelNormalDetails?: string[];
    bowelNotNormalDetails?: string[];
    numberOfTimes?: number;
    numberOfDays?: number;
    notes?: string;
  };
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumOutputQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumOutputQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumOutputs,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumOutputQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreatePostpartumVitalsQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: PostpartumVitalsFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createPostpartumVitalsQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreatePostpartumVitalsQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.PostpartumVitals,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return;

  const updatedQuestionnaireResponse = getPostpartumVitalsQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) {
    return await medplum.updateResource(updatedQuestionnaireResponse);
  }
  return await medplum.createResource(updatedQuestionnaireResponse);
};

type CreateGynecologicalEncounterVitalsSignQuestionnaireResponseParams = {
  medplum: MedplumClient;
  values: GynecologicalEncounterVitalsSignFormType;
  status: QuestionnaireResponse["status"];
  clientId?: string;
  practitionerId?: string;
  encounterId?: string;
  questionnaireResponse?: QuestionnaireResponse;
};

export const createGynecologicalEncounterVitalsSignQuestionnaireResponse = async ({
  medplum,
  values,
  status,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
}: CreateGynecologicalEncounterVitalsSignQuestionnaireResponseParams) => {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.GynecologicalEncounterVitalsSign,
  });

  if (!clientId || !practitionerId || !encounterId || !questionnaire?.id) return undefined;
  const updatedQuestionnaireResponse = getGynecologicalEncounterVitalsSignQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaire.id,
    status,
    questionnaireResponse,
  });

  if (questionnaireResponse?.id) return await medplum.updateResource(updatedQuestionnaireResponse);

  return await medplum.createResource(updatedQuestionnaireResponse);
};

export async function fetchGestationalAge({
  medplum,
  clientId,
  encounter,
}: {
  medplum: MedplumClient;
  clientId: string;
  encounter: Encounter;
}): Promise<number | undefined> {
  if (!encounter.id || !clientId) {
    throw new Error("Encounter ID or client ID is required");
  }
  try {
    const observations = await medplum.searchResources(
      "Observation",
      {
        subject: joinReferenceString("Patient", clientId),
        encounter: joinReferenceString("Encounter", encounter.id!),
        code: joinTokenString(Systems.LOINC, LOINCCodes.GestationalAge),
        _sort: "-_lastUpdated",
      },
      {
        cache: "no-cache",
      },
    );

    if (observations.length > 0) {
      const gestationalAge = observations[0].valueQuantity?.value;
      if (gestationalAge) {
        const gestationalAgeInWeeks = decimalWeeksToWeeksDays(gestationalAge);
        return gestationalAgeInWeeks.weeks;
      }
    }
  } catch (error) {
    console.error("Error fetching gestational age:", error);
  }
  return undefined;
}
export const fetchEncounterProviderName = async (
  medplum: MedplumClient,
  participants: EncounterParticipant[] | undefined,
): Promise<string | undefined> => {
  const providerNames = await Promise.all(
    participants
      ?.filter((participant) =>
        participant.type?.some((type) =>
          type.coding?.some(
            (coding) =>
              coding.code === EncounterPractitionerType.Provider &&
              coding.system === QuiltedHealthCodeSystems.EncounterPractitionerType,
          ),
        ),
      )
      ?.map(async (participant) => await getDisplayStringAsync(medplum, participant.individual))
      ?.filter(Boolean) ?? [],
  );

  return providerNames[0]?.trim();
};
